/**
 * Platform announcements, as the tenant sees them: a popup once, then a strip
 * that stays until the announcement's own end time — or, for an "appear once"
 * notice, until the reader clears it.
 *
 * TWO SURFACES, ONE FETCH. The server returns every LIVE announcement for this
 * user, dismissed ones included, each carrying `dismissed` and `dismissible` —
 * so the popup renders the first undismissed row and the strip renders the rest
 * of them. The list is not filtered server-side because the two surfaces want
 * different subsets of the same rows.
 *
 * DISMISSAL MEANS TWO DIFFERENT THINGS, on purpose:
 *   - dismissible=false (a maintenance window): dismissing silences the POPUP
 *     and the strip stands until ends_at. Nobody may lose that notice by
 *     clicking through it.
 *   - dismissible=true (a "new features" notice): dismissing clears it for that
 *     user for good, both surfaces. A new announcement is a new row, so it
 *     shows again.
 * Dismissal is per-user in announcement_dismissals either way, so one reader
 * clearing it never clears anyone else's.
 *
 * NOTHING EXPIRES CLIENT-SIDE. A row stops matching the server's window, so it
 * simply stops arriving and both surfaces empty together. No cron, no cleanup,
 * and — the reason this matters — no client clock: a workshop tablet with the
 * wrong date cannot make a maintenance notice vanish early or linger for a week.
 *
 * POLLED, NOT PUSHED. The same idiom the notification bell already uses, at
 * five minutes, because this content is scheduled hours ahead.
 *
 * MOUNTED IN Layout, NOT App. App-level siblings (the toaster, the version
 * notifier) render outside all route gating, which includes the public
 * customer-facing quotation, invoice and payment-link pages reached from an
 * SMS. A platform maintenance notice on a customer's payment link would be a
 * message to the wrong audience entirely.
 */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FiInfo, FiAlertTriangle, FiAlertOctagon, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Modal, Button } from './ui';
import useVisiblePolling from '../hooks/useVisiblePolling';
import { BACKGROUND_READ_TIMEOUT_MS } from '../utils/readTimeout';

// Appearance only — severity buys no extra powers. See models/Announcement.js.
const TONE = {
  info: {
    icon: FiInfo,
    strip: 'border-sky-200/70 dark:border-sky-800/60 bg-sky-50/80 dark:bg-sky-900/20',
    rail: 'bg-sky-500',
    chip: 'bg-sky-100 dark:bg-sky-900/50 text-sky-600 dark:text-sky-300',
    mark: 'text-sky-600 dark:text-sky-400',
  },
  warning: {
    icon: FiAlertTriangle,
    strip: 'border-amber-200/70 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-900/20',
    rail: 'bg-amber-500',
    chip: 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-300',
    mark: 'text-amber-600 dark:text-amber-400',
  },
  critical: {
    icon: FiAlertOctagon,
    strip: 'border-rose-200/70 dark:border-rose-800/60 bg-rose-50/80 dark:bg-rose-900/20',
    rail: 'bg-rose-500',
    chip: 'bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-300',
    mark: 'text-rose-600 dark:text-rose-400',
  },
};

const toneOf = (severity) => TONE[severity] || TONE.info;

// The strip is a HEADLINE, not the notice.
//
// Clamping by LINES was the first attempt and it quietly failed on the very
// screen it was written for: on a wide dashboard one line holds the entire
// message, so `line-clamp-1` clamped nothing and the reader got the wall of
// text it was meant to prevent. Characters do not depend on the viewport.
//
// Whitespace is flattened first because operator prose carries the line breaks
// of a maintenance schedule, and a preview that keeps them is a paragraph.
// The full text — breaks intact — is one click away in the modal.
const PREVIEW_CHARS = 90;

function preview(text) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= PREVIEW_CHARS) return clean;
  const cut = clean.slice(0, PREVIEW_CHARS);
  const space = cut.lastIndexOf(' ');
  // Break on a word where one is near the end, but never hand back a stub:
  // a long unbroken string (a URL, a run of Arabic with no space) keeps the
  // hard cut rather than collapsing to three characters and an ellipsis.
  // Strip a trailing LONE HIGH SURROGATE: slicing at a fixed index can land
  // mid-emoji, and half a code point renders as a replacement glyph.
  const kept = (space > PREVIEW_CHARS / 2 ? cut.slice(0, space) : cut)
    .replace(/[\uD800-\uDBFF]$/, '')
    .trimEnd();
  return `${kept}\u2026`;
}

// The environment label, rendered only when it is not the assumed one.
// Label only — it never affects which announcements a reader is shown; that is
// decided server-side in announcementController.visibleToWhere().
function EnvBadge({ environment, t }) {
  if (!environment || environment === 'prod') return null;
  const key = `ann.env${environment.charAt(0).toUpperCase()}${environment.slice(1)}`;
  return (
    <span className="me-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 align-middle text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
      {t(key)}
    </span>
  );
}

const POLL_MS = 5 * 60 * 1000;

export default function AnnouncementBar() {
  const { i18n, t } = useTranslation();
  const isAr = (i18n.language || '').slice(0, 2) === 'ar';
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  // The row the reader OPENED from the strip, as opposed to the one the popup
  // raised by itself. Separate state because reading an old notice again must
  // not be mistaken for acknowledging the newest unread one.
  const [opened, setOpened] = useState(null);

  const envBadge = useCallback(
    (row) => <EnvBadge environment={row.environment} t={t} />,
    [t],
  );

  const pick = useCallback(
    (row, field) => (isAr ? row[`${field}_ar`] : row[`${field}_en`]) || row[`${field}_en`] || row[`${field}_ar`] || '',
    [isAr],
  );

  const load = useCallback(async () => {
    try {
      // Bounded (B02): the poller runs one read at a time, and a hang here
      // would end the strip's refresh for the whole session. The current
      // notices stay on screen through a timeout.
      const res = await axios.get('/api/announcements', {
        timeout: BACKGROUND_READ_TIMEOUT_MS,
      });
      setItems(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      // Silent. An announcement that fails to load is not something the user
      // did, and a toast here would fire on every poll while the API is down —
      // on top of whatever the outage itself is already doing to the screen.
    }
  }, []);

  // Visible-tab only (P01). A five-minute strip refresh has no reason to run in
  // a background tab; coming back re-reads when the copy on screen is already
  // a full cycle old, so a returning reader still sees current notices.
  useVisiblePolling(load, POLL_MS);

  const unread = items.find((a) => !a.dismissed) || null;
  // An "appear once" row leaves the strip the moment its reader clears it. One
  // that is not dismissible stays put — that is the whole difference, and it is
  // decided by the operator when publishing, never here.
  const strip = items.filter((a) => !(a.dismissible && a.dismissed));

  const markDismissed = async (row) => {
    if (!row) return;
    setSaving(true);
    // Optimistic, and NOT reverted on failure. The alternative is a popup that
    // reappears in the user's face because a network call failed — for an
    // acknowledgement that costs nothing if it is lost. The next poll re-reads
    // the server's opinion of what has been dismissed.
    setItems((prev) => prev.map((a) => (a.id === row.id ? { ...a, dismissed: true } : a)));
    try {
      await axios.post(`/api/announcements/${row.id}/dismiss`);
    } catch {
      /* see above */
    } finally {
      setSaving(false);
    }
  };

  const dismissPopup = () => markDismissed(unread);

  if (!items.length) return null;

  // The modal serves both entries. `opened` wins so that reading an older
  // notice does not silently acknowledge the newest one behind it.
  const shown = opened || unread;
  const Chevron = isAr ? FiChevronLeft : FiChevronRight;

  return (
    <>
      {/* The gap belongs HERE, not in Layout: a margin on the Layout slot would
          leave a hole above the page heading on every screen with no notice.
          But the `!items.length` guard above is NOT enough to earn it —
          `listMine` keeps returning a dismissed row (flagged) for the notice's
          whole life, so an appear-once notice that its reader has cleared
          leaves items=[row] and strip=[], and the wrapper rendered a childless
          20px hole on every page for the rest of that announcement's life.
          Gate the wrapper on what it actually renders. The Modal stays outside
          it: `unread` is a subset of `strip`, so an empty strip has nothing to
          pop up anyway, but tying the dialog's existence to the strip's would
          be a second rule to keep in step. */}
      {strip.length > 0 && (
      <div className="mb-5 space-y-2">
        {strip.map((a) => {
          const tone = toneOf(a.severity);
          const Icon = tone.icon;
          return (
            <div
              key={a.id}
              role="status"
              className={`relative flex items-center gap-2 overflow-hidden rounded-xl border ps-4 pe-3 py-3
                          text-sm shadow-sm ${tone.strip}`}
            >
              {/* A coloured rail rather than a tinted background alone: it reads
                  as severity at a glance without shouting, and survives the
                  glass skin's translucency. */}
              <span className={`absolute inset-y-0 start-0 w-1 ${tone.rail}`} aria-hidden="true" />

              {/* The opener is a real BUTTON and a sibling of the dismiss
                  button, rather than the whole strip carrying role="button"
                  with another button nested inside it. Nested interactive
                  elements are an accessibility fault, and the outer role would
                  also have cost the live region that announces this to a screen
                  reader when it arrives. */}
              <button
                type="button"
                onClick={() => setOpened(a)}
                className="flex min-w-0 flex-1 items-center gap-3 text-start rounded-lg
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
              >
                <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${tone.chip}`}>
                  <Icon size={18} />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-content truncate">
                    {/* Only a NON-prod notice is badged. Stamping "Production"
                        on every banner is noise the reader learns to skip, and
                        the mistake worth preventing is the other direction: a
                        development window read as a live outage. */}
                    {envBadge(a)}
                    {pick(a, 'title')}
                  </span>
                  {/* A teaser, capped in CHARACTERS — see preview(). truncate
                      stays as the belt-and-braces for a narrow screen, where
                      even 90 characters overflow. */}
                  <span className="mt-0.5 block truncate text-content-secondary">
                    {preview(pick(a, 'body'))}
                  </span>
                </span>

                {/* A pill rather than bare tinted words: the strip is the one
                    surface where "there is more of this" has to read as
                    clickable at a glance. It stays at every width — on a phone
                    the label drops and the chevron alone carries it, because
                    that is the width where a truncated notice is most likely
                    to be mistaken for the whole message. */}
                <span
                  className={`shrink-0 inline-flex items-center gap-1 rounded-full border
                              border-black/10 bg-white/70 px-2.5 py-1 text-xs font-semibold
                              dark:border-white/15 dark:bg-white/5 ${tone.mark}`}
                >
                  <span className="hidden sm:inline">{t('ann.readMore')}</span>
                  <Chevron size={14} />
                </span>
              </button>

              {/* Only an "appear once" row can be cleared from here. On anything
                  else the X would promise something the server will not honour:
                  the strip comes straight back on the next poll. */}
              {a.dismissible && (
                <button
                  type="button"
                  onClick={() => markDismissed(a)}
                  aria-label={t('ann.dontShowAgain')}
                  title={t('ann.dontShowAgain')}
                  className="shrink-0 rounded-lg p-1.5 text-content-muted transition-colors
                             hover:bg-black/5 dark:hover:bg-white/10 hover:text-content
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
                >
                  <FiX size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* closeOnBackdrop stays false (the Modal default) so a maintenance notice
          cannot be cleared by a stray click on the way to something else. */}
      <Modal
        isOpen={!!shown}
        onClose={() => (opened ? setOpened(null) : dismissPopup())}
        title={shown ? pick(shown, 'title') : ''}
        size="md"
        dismissible={false}
        footer={
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 w-full">
            {shown?.dismissible && (
              <Button
                variant="secondary"
                loading={saving}
                data-testid="announcement-dont-show"
                onClick={async () => {
                  await markDismissed(shown);
                  setOpened(null);
                }}
              >
                {t('ann.dontShowAgain')}
              </Button>
            )}
            <Button
              loading={saving}
              data-testid="announcement-dismiss"
              onClick={() => (opened ? setOpened(null) : dismissPopup())}
            >
              {t('ann.gotIt')}
            </Button>
          </div>
        }
      >
        {shown && (
          <div className="flex items-start gap-3">
            {(() => {
              const tone = toneOf(shown.severity);
              const Icon = tone.icon;
              return (
                <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${tone.chip}`}>
                  <Icon size={18} />
                </span>
              );
            })()}
            {/* whitespace-pre-line: an operator writing a maintenance window
                types line breaks, and collapsing them turns a schedule into a
                paragraph. Scrolls rather than growing the dialog past the
                viewport — this is the surface that has to hold a long notice. */}
            <p className="max-h-[60vh] overflow-y-auto whitespace-pre-line text-sm leading-relaxed text-content-secondary">
              {pick(shown, 'body')}
            </p>
          </div>
        )}
      </Modal>
    </>
  );
}
